import React, { useEffect, useState, useCallback } from 'react';
import { useMinMaxContext } from '@/context/minMaxContext/MinMaxContextProvider';
import { Button, Input, Typeahead } from '../../../components';
import { useProductGroup } from '@/context/ProductGroupContext/ProductGroupContextProvider';

/**
 * CreateMinMaxSetting component for creating or updating Min/Max inventory settings.
 *
 * Renders a form with fields for selecting a Power BI Main Product Group, entering Product Type,
 * and specifying Min and Max values. Handles form validation, submission, and error display.
 * Integrates with context for state management and API calls.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.closeModal - Function to close the modal dialog.
 * @param {Object} [props.data] - Existing min/max setting data for editing (if any).
 *
 * @returns {JSX.Element} The rendered form for creating or updating min/max settings.
 */
const CreateMinMaxSetting = ({ closeModal, data }) => {
  const {
    minMaxSettings: minMaxSettingsRaw,
    setMinMaxSettings,
    error,
    createMinMaxSettings,
    updateMinMaxSettings,
    clearFormData,
  } = useMinMaxContext();
  // Always ensure minMaxSettings is an object
  const minMaxSettings = minMaxSettingsRaw || {};
  const [powerBiMainProductGroupQuery, setPowerBiMainProductGroupQuery] =
    useState('');

  const [selectedPowerBiMainProductGroup, setSelectedPowerBiMainProductGroup] =
    useState(
      minMaxSettings?.mainProductGroup?.id
        ? [minMaxSettings.mainProductGroup]
        : [],
    );

  const { powerBiMainProductGroupResponse, getPowerBiMainProductGroup } =
    useProductGroup();

  // Validation states
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (data) setMinMaxSettings(data);
    else setMinMaxSettings({}); // Always set to object, not null
    return () => setMinMaxSettings({});
  }, [data]);

  useEffect(() => {
    getPowerBiMainProductGroup();
  }, []);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setMinMaxSettings((prev) => ({ ...(prev || {}), [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormError('');
  };

  // Validation logic similar to BTS School
  const validateForm = () => {
    let errors = {};
    if (
      !minMaxSettings.mainProductGroup ||
      !minMaxSettings.mainProductGroup.id
    ) {
      errors.mainProductGroup = 'Main Product Group is required.';
    }
    if (
      !minMaxSettings.productType ||
      minMaxSettings.productType.trim() === ''
    ) {
      errors.productType = 'Product Type is required.';
    }
    if (
      minMaxSettings.min === undefined ||
      minMaxSettings.min === '' ||
      isNaN(Number(minMaxSettings.min))
    ) {
      errors.min = 'Min is required and must be a number.';
    }
    if (
      minMaxSettings.max === undefined ||
      minMaxSettings.max === '' ||
      isNaN(Number(minMaxSettings.max))
    ) {
      errors.max = 'Max is required and must be a number.';
    }
    setFieldErrors(errors);
    setFormError(
      Object.keys(errors).length > 0 ? 'Please fix the errors above.' : '',
    );
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setFormError('');
    setFieldErrors({});
    if (data) {
      await updateMinMaxSettings(data.id, minMaxSettings, handleClose);
    } else {
      await createMinMaxSettings(minMaxSettings, handleClose);
    }
  };

  const handleClose = () => {
    closeModal();
    clearFormData();
  };

  const handleTypeaheadChange = useCallback(
    (field, setSelected, setQuery) => (selectedOptions) => {
      const selectedOption = selectedOptions[0];
      setSelected(selectedOptions);
      setMinMaxSettings((prev) => ({
        ...(prev || {}),
        [field]: selectedOption
          ? {
              id: selectedOption.id,
              [`${field}Name`]: selectedOption[`${field}Name`],
            }
          : null,
      }));
      setQuery(selectedOption ? selectedOption[`${field}Name`] : '');
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      setFormError('');
    },
    [setMinMaxSettings],
  );

  const handleTypeaheadBlur = useCallback(
    (field, setSelected, setQuery, query, response) => (e) => {
      if (query || setSelected[field] == query) {
        setSelected([]);
        setQuery('');
        setMinMaxSettings((prev) => ({ ...(prev || {}), [field]: null }));
      }
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      setFormError('');
    },
    [setMinMaxSettings],
  );

  useEffect(() => {
    if (data) {
      setSelectedPowerBiMainProductGroup(
        data.mainProductGroup.id ? [data.mainProductGroup] : [],
      );
    }
  }, [data]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-2 gap-4">
        <Typeahead
          id="mainProductGroup"
          label="Power BI Main Product Group"
          labelKey="powerBiMainProductGroupName"
          options={powerBiMainProductGroupResponse}
          placeholder="Choose Power BI Product Group"
          disabled={data?.mainProductGroup?.id}
          onChange={handleTypeaheadChange(
            'mainProductGroup',
            setSelectedPowerBiMainProductGroup,
            setPowerBiMainProductGroupQuery,
          )}
          onBlur={handleTypeaheadBlur(
            'mainProductGroup',
            setSelectedPowerBiMainProductGroup,
            setPowerBiMainProductGroupQuery,
            powerBiMainProductGroupQuery,
            powerBiMainProductGroupResponse,
          )}
          selected={selectedPowerBiMainProductGroup}
          query={powerBiMainProductGroupQuery}
          setQuery={setPowerBiMainProductGroupQuery}
          custErr={fieldErrors.mainProductGroup}
          isInvalid={!!fieldErrors.mainProductGroup}
          disabledCreateNew={true}
        />
        <Input
          label="Product Type"
          name="productType"
          placeholder="Enter Product Type"
          value={
            typeof minMaxSettings?.productType === 'string'
              ? minMaxSettings.productType
              : ''
          }
          onChange={handleInput}
          error={fieldErrors.productType}
          required
        />
        <Input
          label="Min"
          type="number"
          name="min"
          placeholder="Enter Min Value"
          value={
            typeof minMaxSettings?.min === 'number' ||
            typeof minMaxSettings?.min === 'string'
              ? minMaxSettings.min
              : ''
          }
          onChange={handleInput}
          error={fieldErrors.min}
          required
        />
        <Input
          label="Max"
          type="number"
          name="max"
          placeholder="Enter Max Value"
          value={
            typeof minMaxSettings?.max === 'number' ||
            typeof minMaxSettings?.max === 'string'
              ? minMaxSettings.max
              : ''
          }
          onChange={handleInput}
          error={fieldErrors.max}
          required
        />
      </div>
      <div className="my-5 mr-5 flex gap-2 justify-end">
        <Button type="button" onClick={closeModal} variant="secondary">
          Cancel
        </Button>
        <Button type="submit">{data ? 'Update' : 'Save'}</Button>
      </div>
    </form>
  );
};

export default CreateMinMaxSetting;
